import { cn } from '@/lib/utils';

/**
 * Preset "aurora" (fondo claro, el default recomendado): tres blobs
 * radial-gradient desenfocados en violeta / verde / celeste con keyframes
 * lentos, más un "spot" que sigue al cursor. El wrapper de cada blob se
 * desplaza con --mx/--my (transition .6s) y el hijo lleva la animación: así
 * el puntero y los keyframes no se pisan el transform. Degradé blanco abajo
 * para que el texto siempre lea.
 */
export function AuroraBackground({ mood = 'calm', className }: { mood?: 'calm' | 'bold'; className?: string }) {
  const bold = mood === 'bold';
  const blob = (color: string, pos: string, size: string, anim: string, factor: number, opacity: number) => (
    <div
      className="absolute pl-follow"
      style={{
        ...toStyle(pos),
        transform: `translate(calc((var(--mx, .5) - .5) * ${80 * factor}px), calc((var(--my, .5) - .5) * ${60 * factor}px))`,
      }}
    >
      <div
        className={cn('rounded-full blur-2xl', anim, bold && 'pl-bold')}
        style={{ width: size, height: size, background: `radial-gradient(closest-side, ${color}, transparent)`, opacity }}
      />
    </div>
  );
  return (
    <div className={cn('absolute inset-0 overflow-hidden bg-white dark:bg-brand-dark', className)} aria-hidden>
      {blob('#CEABFF', 'left:-12%;top:-30%', '52vw', 'pl-aurora-1', 1, bold ? 0.9 : 0.7)}
      {blob('#1CC9B9', 'right:-14%;top:-20%', '50vw', 'pl-aurora-2', -1, bold ? 0.8 : 0.55)}
      {blob('#87DBFF', 'left:34%;bottom:-45%', '48vw', 'pl-aurora-3', 0.6, bold ? 0.85 : 0.6)}
      {/* Spot que sigue directo al cursor */}
      <div
        className="absolute h-[28vw] w-[28vw] rounded-full blur-[48px] pl-follow"
        style={{
          left: 'calc(var(--mx, .5) * 100% - 14vw)',
          top: 'calc(var(--my, .5) * 100% - 14vw)',
          background: 'radial-gradient(closest-side, rgba(28,201,185,.55), transparent)',
          opacity: bold ? 0.9 : 0.6,
        }}
      />
      <div className="absolute inset-0 bg-linear-to-b from-transparent via-white/30 to-white/85 dark:via-brand-dark/30 dark:to-brand-dark/85" />
    </div>
  );
}

function toStyle(pos: string): React.CSSProperties {
  const out: Record<string, string> = {};
  for (const part of pos.split(';')) {
    const [k, v] = part.split(':');
    if (k && v) out[k.trim()] = v.trim();
  }
  return out as React.CSSProperties;
}
